/* eslint-disable react/no-danger */
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import VideoDetail from '../Video/VideoDetail';
import VirtualTourDetail from '../VirtualTour/VirtualTourDetail';
import PhotoGalleryDetail from '../PhotoGallery/PhotoGalleryDetail/PhotoGalleryDetail';
import useDetailViewTracking from '../../hooks/useDetailViewTracking';
import useContentLinkTracking from '../../hooks/useContentLinkTracking';

import './AboutTab.scss';

// The optional rich text authored on a media item. An image carries Drupal's
// long-text "Body copy" field (bodyCopyHTML) and renders it as body copy beneath
// the figure; every other media type carries the short "Caption" field
// (captionHTML) and renders it as a caption beneath the embed. Same markup,
// different class, so the caller picks the treatment and the row grid can tell
// the two apart.
const MediaHtml = ({ html, className }) => (html ? (
  <div
    className={className}
    dangerouslySetInnerHTML={{ __html: html }}
  />
) : null);

MediaHtml.propTypes = {
  html: PropTypes.string,
  className: PropTypes.string.isRequired,
};
MediaHtml.defaultProps = { html: null };

// The embed for a non-image media item, delegating to the same detail
// components the "My Media" pane uses (video / gallery / virtual tour) or a
// plain link (external link / document).
const renderMediaEmbed = (item) => {
  switch (item.type) {
    case 'video':
      return (
        <VideoDetail
          videoId={item.video?.serviceVideoId}
          service={item.video?.service}
          className="detailSection"
        />
      );
    case 'gallery':
      return (
        <PhotoGalleryDetail
          images={item.images}
          galleryClass="slideshow"
          imageClass="slideshowPhoto"
          className="detailSection detailSection--gallery"
        />
      );
    case 'virtualTour':
      return (
        <VirtualTourDetail
          threeSixtiesData={item.threeSixties}
          className="detailSection detailSection--virtualTour"
        />
      );
    case 'link':
      return (
        <a
          className="aboutTab__section__link"
          href={item.uri}
          target="_blank"
          rel="noopener noreferrer"
        >
          {item.linkTitle || item.uri}
        </a>
      );
    case 'document':
      return (
        <a
          className="aboutTab__section__document"
          href={item.path}
          target="_blank"
          rel="noopener noreferrer"
        >
          {item.name}
        </a>
      );
    default:
      return null;
  }
};

// Render one media item. Images keep the figure/caption treatment (and take
// part in the row/grid tiling via imageSize); everything else renders as a
// full-width block that carries its own caption.
const renderMediaItem = (item, index, imageSize) => {
  const key = item.url || item.uri || item.path || `${item.type}-${index}`;

  if (item.type === 'image') {
    return (
      <figure
        key={key}
        className={classNames('aboutTab__section__figure', {
          [`aboutTab__section__figure--${imageSize}`]: imageSize,
        })}
      >
        {/* Drupal serves these through an image style, but a section can hold
            several and every tab's sections are rendered up front, so defer
            the fetch until the image is near the viewport. */}
        <img
          className="aboutTab__section__image"
          src={item.url}
          alt={item.alt || ''}
          loading="lazy"
          decoding="async"
        />
        {/* alt is the image caption (detail-note style). */}
        {item.alt && (
          <figcaption className="aboutTab__section__caption">{item.alt}</figcaption>
        )}
        <MediaHtml html={item.bodyCopyHTML} className="aboutTab__section__bodyCopy" />
      </figure>
    );
  }

  return (
    <div
      key={key}
      className={classNames(
        'aboutTab__section__mediaBlock',
        `aboutTab__section__mediaBlock--${item.type}`,
      )}
      data-testid={`about-media:${item.type}`}
    >
      {renderMediaEmbed(item)}
      <MediaHtml html={item.captionHTML} className="aboutTab__section__mediaCaption" />
    </div>
  );
};

// Links and documents authored in the Content column, rendered below the body
// and grouped with it. Up to two show as buttons; more than two as a list.
const renderResources = (resources) => {
  const asList = resources.length > 2;

  const items = resources.map((res, index) => {
    const isDoc = res.type === 'document';
    const href = isDoc ? res.path : res.uri;
    const label = isDoc ? res.name : res.linkTitle || res.uri;
    const key = href || `${res.type}-${index}`;
    const anchor = (
      <a
        key={key}
        className={classNames(
          'aboutTab__section__resource',
          `aboutTab__section__resource--${res.type}`,
          { 'aboutTab__section__resource--button': !asList },
        )}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`about-resource:${res.type}`}
      >
        {label}
      </a>
    );
    return asList ? <li key={key}>{anchor}</li> : anchor;
  });

  return asList ? (
    <ul className="aboutTab__section__resources aboutTab__section__resources--list">{items}</ul>
  ) : (
    <div className="aboutTab__section__resources aboutTab__section__resources--buttons">{items}</div>
  );
};

// Renders the community's About content as a stack of titled sections. Each
// section pairs text with a heterogeneous Media field — images, videos, photo
// galleries, virtual tours, external links, or documents — rendered by type.
// imagePosition (above/below/left/right), imageSize (small/medium/large) and
// imageLayout (column/row/grid) drive the media block's placement, image size,
// and multi-image arrangement. The whole thing is one subpane, so PanesWidget
// hides the subpane buttons and it reads as a single scrollable page.
const AboutTab = ({ data, tracking }) => {
  const { sections, id: bucketId, title: bucketTitle } = data || {};

  const containerRef = useRef(null);
  // This component remounts per bucket subpane (Pane keys it by bucket id), so
  // mount→unmount brackets the dwell on one About bucket.
  useDetailViewTracking({
    tracking, type: 'about', target: bucketId, label: bucketTitle,
  });
  // Delegated <a>-click capture across the section content (body, captions,
  // Content-column resources, media links/documents). Media opens (video /
  // gallery / virtual tour) aren't links, so they aren't captured.
  useContentLinkTracking(containerRef, tracking);

  if (!sections || sections.length === 0) return null;

  return (
    <div className="aboutTab" data-testid="about-tab" ref={containerRef}>
      {sections.map((section, index) => {
        const {
          title, bodyHTML, images, media, resources,
          imagePosition, imageSize, imageLayout, featuredPosition,
        } = section;
        const hasImages = images && images.length > 0;
        // Featured media (video / gallery / virtual tour): full-width, above or
        // below the whole content column.
        const hasFeatured = media && media.length > 0;
        // Row/grid arrangement only applies to Small/Medium with 2+ images.
        const layoutApplies = hasImages
          && images.length > 1
          && (imageSize === 'small' || imageSize === 'medium')
          && imageLayout;

        const featured = hasFeatured && (
          <div className="aboutTab__section__featured">
            {media.map((item, itemIndex) => renderMediaItem(item, itemIndex, imageSize))}
          </div>
        );

        return (
          <div
            key={section.id || index}
            className="detailSection aboutTab__section"
            data-testid={`about-section:${section.id || index}`}
          >
            {hasFeatured && featuredPosition !== 'below' && featured}

            <div
              className={classNames('aboutTab__section__content', {
                [`aboutTab__section--${imagePosition}`]: hasImages && imagePosition,
                'aboutTab__section--noMedia': !hasImages,
              })}
            >
              {hasImages && (
                <div
                  className={classNames('aboutTab__section__media', {
                    [`aboutTab__section__media--${imageLayout}`]: layoutApplies,
                  })}
                >
                  {images.map((item, itemIndex) => renderMediaItem(item, itemIndex, imageSize))}
                </div>
              )}

              <div className="aboutTab__section__text">
                {title && <div className="detailHeader aboutTab__section__header"><h2 className="detailHeader__title">{title}</h2></div>}

                {bodyHTML && (
                  <div
                    className="aboutTab__section__body"
                    dangerouslySetInnerHTML={{ __html: bodyHTML }}
                  />
                )}

                {resources && resources.length > 0 && renderResources(resources)}
              </div>
            </div>

            {hasFeatured && featuredPosition === 'below' && featured}
          </div>
        );
      })}
    </div>
  );
};

AboutTab.propTypes = {
  data: PropTypes.shape({
    sections: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        bodyHTML: PropTypes.string,
        // Inline images (Content column), keyed by `type: 'image'`.
        images: PropTypes.arrayOf(PropTypes.object),
        // Featured media (video / gallery / virtual tour), keyed by `type`.
        media: PropTypes.arrayOf(
          PropTypes.shape({
            type: PropTypes.oneOf(['image', 'video', 'gallery', 'virtualTour', 'link', 'document']),
            // Rich text on a non-image item; images use bodyCopyHTML instead.
            captionHTML: PropTypes.string,
            // image
            url: PropTypes.string,
            alt: PropTypes.string,
            bodyCopyHTML: PropTypes.string,
            // video
            video: PropTypes.shape({
              service: PropTypes.string,
              serviceVideoId: PropTypes.string,
            }),
            // gallery
            images: PropTypes.arrayOf(PropTypes.object),
            // virtual tour
            threeSixties: PropTypes.arrayOf(PropTypes.object),
            // link
            uri: PropTypes.string,
            linkTitle: PropTypes.string,
            // document
            name: PropTypes.string,
            path: PropTypes.string,
          }),
        ),
        // Links and documents, rendered below the body as buttons or a list.
        resources: PropTypes.arrayOf(PropTypes.object),
        imagePosition: PropTypes.string,
        imageSize: PropTypes.string,
        imageLayout: PropTypes.string,
        // Featured-media placement relative to the content column.
        featuredPosition: PropTypes.oneOf(['above', 'below']),
      }),
    ),
  }),
  tracking: PropTypes.shape({
    endpoint: PropTypes.string,
    token: PropTypes.string,
    identity: PropTypes.shape({
      residentNid: PropTypes.number,
      contactHash: PropTypes.string,
      communityNid: PropTypes.number,
    }),
  }),
};

AboutTab.defaultProps = {
  data: { sections: [] },
  tracking: null,
};

export default AboutTab;
